import { BackofficeNavGroup } from "@/backoffice/components/layouts/BackofficeNavGroup";
import { BackofficeNavLeaf } from "@/backoffice/components/layouts/BackofficeNavLeaf";
import {
  ICON_HOVER,
  ICON_POP,
} from "@/backoffice/components/layouts/backofficeIconMotion";
import { BackofficeNavText } from "@/backoffice/components/layouts/BackofficeNavText";
import { BackofficeNavSectionTitle } from "@/backoffice/components/layouts/BackofficeNavSectionTitle";
import type {
  NavChild,
  NavIcon,
} from "@/backoffice/components/layouts/backofficeNavItems";
import { Icon } from "@/components";
import type { IconType } from "@/components/icon/icon";
import { ChatRoutes, DashboardRoutes } from "@/constants/routes";
import { useAuth } from "@/context";
import { useUnreadMessageCount } from "@/domain/chat/useCases/useUnreadMessageCount";
import { postHogIdentifyUser } from "@/lib/posthogClient";
import { featuresService } from "@/services/featuresService";
import { useSidebarStore } from "@/store/sidebarStore";
import { cn, numberUtils } from "@/utils";
import { authSession } from "@/utils/authSession";
import { getRoutes } from "@/utils/routeMetadata";
import {
  Circle,
  LayoutDashboard,
  MessageSquare,
  PanelLeftClose,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { CampaignGuard } from "../campaignGuard/campaignGuard";
import { FidexaPayGuard } from "../fidexaPayGuard/fidexaPayGuard";
import { FeatureGuard } from "../FeatureGuard";
import { NavPopover } from "./NavPopover";

type NavigationProps = {
  isMobile?: boolean;
  toggleMenu?: () => void;
};

// Features exigidas para exibir menus do tipo dropdown (com children).
// Menus não listados aqui permanecem visíveis (sem gating por feature).
const DROPDOWN_FEATURE: Record<string, string> = {
  reports: "read:dashboard:home",
  fidexaPay: "read:dashboard:home",
  campaigns: "read:campaign",
};

// Features exigidas por FILHO de dropdown. Só as chaves listadas aqui são
// gateadas individualmente — filhos ausentes seguem herdando o gate do pai,
// como sempre foi para Relatórios/Fidexa Pay.
const CHILD_FEATURE: Record<string, string> = {
  [DashboardRoutes.CAMPAIGNS]: "read:campaign",
  [DashboardRoutes.CAMPAIGNS_SHEET_TEMPLATES]: "write:campaign:sheet-template",
  [DashboardRoutes.CAMPAIGNS_RESULTS]: "read:campaign:result",
  [DashboardRoutes.BILLING_RESULTS]: "read:billing-range:result",
  [DashboardRoutes.BILLING_QUEUE]: "read:billing-range:queue",
};

const STRING_ICONS: Record<string, NavIcon> = {
  dashboard: LayoutDashboard,
  chat: MessageSquare,
  person: Users,
  user: User,
};

const fallbackIcons = new Map<string, NavIcon>();

/** A rota traz o ícone como nome (SVG do projeto) ou como componente; o menu usa sempre componente. */
const toNavIcon = (icon: IconType | LucideIcon): NavIcon => {
  if (typeof icon !== "string") return icon;
  if (STRING_ICONS[icon]) return STRING_ICONS[icon];
  let wrapped = fallbackIcons.get(icon);
  if (!wrapped) {
    wrapped = ({ className }) => <Icon icon={icon} className={className} />;
    fallbackIcons.set(icon, wrapped);
  }
  return wrapped;
};

function getFeatureByRouteKey(key: string): string {
  const childFeature = CHILD_FEATURE[key];
  if (childFeature) return childFeature;

  switch (key) {
    case DashboardRoutes.DASHBOARD:
      return "read:dashboard:home";
    case DashboardRoutes.CUSTOMERS:
      return "read:customer";
    case DashboardRoutes.USERS:
      return "read:user";
    case DashboardRoutes.SETTINGS:
      return "read:dashboard:home";
    case DashboardRoutes.CHANGE_PASSWORD:
      return "read:dashboard:home";
    case DashboardRoutes.REPORTS_SEND_MESSAGES:
      return "read:report:send_messages";
    case DashboardRoutes.CHAT:
      return "read:chat";
    case DashboardRoutes.BILLING_MANAGEMENT:
      return "read:dashboard:home";
    case DashboardRoutes.IMPORT_SHEETS:
      return "read:dashboard:home";
    case DashboardRoutes.MESSAGE_MANAGER:
      return "read:dashboard:home";
    default:
      break;
  }
  return key;
}

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, "");

/**
 * O menu do dashboard, montado com os mesmos componentes do menu do backoffice
 * (item, grupo, título de seção, texto que recolhe animado). Só o visual é
 * compartilhado: rotas, permissões, "Em breve", contador do chat e logout
 * seguem as regras do dashboard.
 */
export const Navigation = ({
  isMobile = false,
  toggleMenu,
}: NavigationProps) => {
  const [userName, setUserName] = useState<string>("");

  const location = useLocation();
  const pathname = location.pathname;
  const { logout, featuresLoaded, fidexaModules } = useAuth();
  const { unreadMessageCount } = useUnreadMessageCount();
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]);
  const { isCollapsed: storeCollapsed, setCollapsed } = useSidebarStore();
  const isCollapsed = storeCollapsed && !isMobile;

  // Usuário sem nenhuma permissão não tem itens de menu para navegar:
  // colapsa a sidebar para não exibir um menu vazio expandido.
  useEffect(() => {
    if (!featuresLoaded) return;
    if (featuresService.getFeatures().length === 0) {
      setCollapsed(true);
    }
  }, [featuresLoaded, setCollapsed]);

  const { routes } = getRoutes({ isMobile, fidexaModules });

  const toggleDropdown = (key: string) => {
    setOpenDropdowns(prev =>
      prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    );
  };

  const isDropdownOpen = (
    key: string,
    children?: Array<{ key: string; path: string; title: string }>
  ) => {
    const normalizedPathname = stripTrailingSlash(pathname);
    const isManuallyOpen = openDropdowns.includes(key);
    const isPathInChildren = children?.some(
      child => normalizedPathname === stripTrailingSlash(child.path)
    );

    return Boolean((isManuallyOpen || isPathInChildren) && children?.length);
  };

  useEffect(() => {
    setUserName(authSession.getUserName() || "");
  }, []);

  postHogIdentifyUser(userName);

  // Fecha a gaveta do celular depois de navegar (o pequeno atraso deixa o clique registrar).
  const closeMobileMenu = () => {
    if (isMobile && toggleMenu) setTimeout(() => toggleMenu(), 100);
  };

  const renderNavigationItems = () => {
    const normalizedPathname = stripTrailingSlash(pathname);
    let previousSection: string | undefined;

    return routes.flatMap(
      ({ key, icon, path, title, children, isDevelopment, section }) => {
        const IconN = toNavIcon(icon);
        const linkPath = key === DashboardRoutes.CHAT ? ChatRoutes.CHAT : path;
        const isSelected =
          linkPath !== "" &&
          normalizedPathname === stripTrailingSlash(linkPath);

        const items = children || [];
        const hasChildren = children && children.length > 0;
        // "Em breve": navegação liberada só em desenvolvimento (import.meta.env.DEV);
        // em produção o item fica visível com a tag "EM BREVE" mas não navega.
        const isDevelopmentLocked =
          Boolean(isDevelopment) && !import.meta.env.DEV;

        const showSectionHeader = !!section && section !== previousSection;
        previousSection = section;
        const sectionHeaderNode = showSectionHeader ? (
          <BackofficeNavSectionTitle
            key={`section-${section}`}
            isCollapsed={isCollapsed}
          >
            {section}
          </BackofficeNavSectionTitle>
        ) : null;

        if (hasChildren) {
          const dropdownFeature = DROPDOWN_FEATURE[key];

          // Sem isto, quem tem read:campaign mas não read:campaign:result via
          // "Resultado" no menu e era jogado de volta pro Dashboard ao clicar.
          const visibleItems = items.filter(item => {
            const childFeature = CHILD_FEATURE[item.path];
            return !childFeature || featuresService.hasFeature(childFeature);
          });

          const navChildren: NavChild[] = visibleItems.map(child => ({
            label: child.title,
            path: child.path,
            icon: Circle,
            comingSoon: isDevelopment,
            isActive: normalizedPathname === stripTrailingSlash(child.path),
            onSelect: () => {
              closeMobileMenu();
              if (child.key === DashboardRoutes.LOGOUT) logout();
            },
          }));

          const dropdownNode = isCollapsed ? (
            <NavPopover
              key={key}
              icon={IconN}
              title={title}
              children={visibleItems}
              isSelected={isSelected}
            />
          ) : (
            <BackofficeNavGroup
              key={key}
              label={title}
              icon={IconN}
              items={navChildren}
              isActive={navChildren.some(child => child.isActive)}
              isOpen={isDropdownOpen(key, children) && !isDevelopmentLocked}
              isCollapsed={false}
              isLocked={isDevelopmentLocked}
              lockedHint="Não contratado pela sua empresa"
              pathname={pathname}
              onToggle={() => {
                if (!isDevelopmentLocked) toggleDropdown(key);
              }}
              onNavigate={() => undefined}
            />
          );

          // Campanha tem gate duplo (permissão + flag por empresa): usar só
          // o FeatureGuard mostraria o menu para quem tem a permissão mas
          // está fora do allowlist, e toda rota abaixo dele responderia 403.
          if (key === "campaigns") {
            return [
              sectionHeaderNode,
              <CampaignGuard key={key} feature={dropdownFeature}>
                {dropdownNode}
              </CampaignGuard>,
            ];
          }

          // Fidexa Pay: mesmo gate duplo, com a flag fidexa_pay.module. O
          // menu aparece quando ao menos uma loja do usuário está liberada.
          if (key === "fidexaPay") {
            return [
              sectionHeaderNode,
              <FidexaPayGuard key={key} feature={dropdownFeature}>
                {dropdownNode}
              </FidexaPayGuard>,
            ];
          }

          if (dropdownFeature) {
            return [
              sectionHeaderNode,
              <FeatureGuard key={key} feature={dropdownFeature}>
                {dropdownNode}
              </FeatureGuard>,
            ];
          }

          return [sectionHeaderNode, dropdownNode];
        }

        return [
          sectionHeaderNode,
          <FeatureGuard key={key} feature={getFeatureByRouteKey(key)}>
            <BackofficeNavLeaf
              label={title}
              path={linkPath}
              icon={IconN}
              isActive={isSelected}
              isCollapsed={isCollapsed}
              isLocked={isDevelopmentLocked}
              onNavigate={() => {
                closeMobileMenu();
                if (key === "logout") logout();
              }}
            />
          </FeatureGuard>,
        ];
      }
    );
  };

  const unreadLabel = `Chat — ${numberUtils.convertToNumber(unreadMessageCount)} mensagens`;

  const { toggle } = useSidebarStore();
  const isChatActive = pathname.startsWith(ChatRoutes.CHAT);
  const hasUnread = (unreadMessageCount ?? 0) > 0;

  return (
    <nav
      aria-label="Menu"
      className={cn(
        "hermes bg-hermes-bg flex h-full flex-col py-3",
        "transition-[padding] duration-300 ease-out motion-reduce:transition-none",
        // Recolhido, o recuo cresce até a "pílula" do item ficar no meio dos 80px do menu.
        isCollapsed ? "px-[16.5px]" : "px-3"
      )}
    >
      {/* Só os itens rolam; o rodapé (recolher e chat) fica sempre à vista.
          *:shrink-0: sem isto, quando a lista passa da altura da tela o flex
          espreme cada item até a altura do texto (o h-10 vira ~24px). */}
      <div
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto *:shrink-0"
        // Recolhido, uma barra de rolagem espremeria os ícones: continua rolando, sem a barra.
        style={isCollapsed ? { scrollbarWidth: "none" } : undefined}
      >
        {renderNavigationItems()}
      </div>

      {!isMobile && (
        <div className="border-hermes-border mt-3 flex shrink-0 flex-col gap-0.5 border-t pt-3">
          <button
            type="button"
            onClick={toggle}
            aria-label={isCollapsed ? "Expandir menu" : "Recolher menu"}
            aria-expanded={!isCollapsed}
            title={isCollapsed ? "Expandir menu" : undefined}
            className="group hermes-focus-ring text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg flex h-10 w-full cursor-pointer items-center rounded-xl px-3.5 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none"
          >
            <span
              className={cn(
                "grid shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none",
                isCollapsed && "rotate-180"
              )}
            >
              <PanelLeftClose
                size={18}
                className="transition-transform duration-200 ease-out group-hover:scale-110 motion-reduce:transition-none"
              />
            </span>
            <BackofficeNavText visible={!isCollapsed}>
              Recolher menu
            </BackofficeNavText>
          </button>

          <FeatureGuard feature="read:chat">
            <Link
              to={ChatRoutes.CHAT}
              aria-label={isCollapsed ? unreadLabel : undefined}
              title={isCollapsed ? unreadLabel : undefined}
              aria-current={isChatActive ? "page" : undefined}
              className={cn(
                "group hermes-focus-ring relative flex h-12 cursor-pointer items-center rounded-xl px-3.5 text-sm",
                "transition-colors duration-150 motion-reduce:transition-none",
                isChatActive
                  ? "bg-hermes-accent-soft text-hermes-accent"
                  : "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg"
              )}
            >
              <span className="relative shrink-0">
                <MessageSquare
                  key={isChatActive ? "on" : "off"}
                  size={18}
                  className={cn(
                    "transition-[fill] duration-300",
                    ICON_HOVER,
                    isChatActive && cn("fill-current/20", ICON_POP)
                  )}
                />
                {hasUnread && (
                  <span
                    className={cn(
                      "bg-hermes-primary ring-hermes-bg absolute -top-1 -right-1 size-2 rounded-full ring-2",
                      "transition-opacity duration-300 motion-reduce:transition-none",
                      isCollapsed ? "opacity-100" : "opacity-0"
                    )}
                  />
                )}
              </span>
              <BackofficeNavText visible={!isCollapsed}>
                <span className="flex flex-col leading-tight">
                  <span className="text-hermes-fg font-semibold">Chat</span>
                  <span className="text-xs">
                    {numberUtils.convertToNumber(unreadMessageCount)} mensagens
                  </span>
                </span>
              </BackofficeNavText>
              {hasUnread && (
                <BackofficeNavText trailing visible={!isCollapsed}>
                  <span
                    className="bg-hermes-primary block size-2 rounded-full"
                    title="Mensagens não lidas"
                  />
                </BackofficeNavText>
              )}
            </Link>
          </FeatureGuard>
        </div>
      )}
    </nav>
  );
};
